import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useActivacio, useActivacions } from '@/api/activacions';
import { descarregaInformeActivacio, useTracabilitat } from '@/api/informes';
import { Icona } from '@/components/Icona';
import { useFormat, useIdioma } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import { BadgeEstat, BadgeTipus, Buit } from '@/pages/operacio/comu';
import { colorEvent, useEtiquetaEvent, useEtiquetaIndicador, useTemps } from './suport';
import type { EventTracabilitat } from '@/api/informes';
import './informes.css';

const COLOR_COMPLIMENT = { pendent: 'c-gray', si: 'c-green', no: 'c-red' } as const;

function colorCompliment(complert?: boolean | null): string {
  if (complert == null) return COLOR_COMPLIMENT.pendent;
  return complert ? COLOR_COMPLIMENT.si : COLOR_COMPLIMENT.no;
}

const prefix = (pre: string) => (c: EventTracabilitat[]) =>
  c.filter((e) => e.tipus.startsWith(pre)).length;

/** Files del resum d'evidències: clau de traducció (w45.*) i recompte. */
const RESUM: [string, (c: EventTracabilitat[]) => number][] = [
  ['esdeveniments', (c) => c.length],
  ['enviaments', (c) => prefix('DESTINATARI_ENVIAT')(c) + prefix('ENVIAMENT')(c)],
  ['lliuraments', prefix('DESTINATARI_LLIURAT')],
  ['lectures', prefix('DESTINATARI_LLEGIT')],
  ['confirmacions', prefix('DESTINATARI_CONFIRMAT')],
  ['missatges', prefix('MISSATGE')],
  ['escalats', prefix('ESCALAT')],
];

function Cerca() {
  const { t } = useTranslation('informes');
  const navigate = useNavigate();
  const [text, setText] = useState('');
  const [cerca, setCerca] = useState('');
  const resultats = useActivacions({ text: cerca, page: 0, size: 10 }, cerca.length > 0);
  const recents = useActivacions({ page: 0, size: 10 }, cerca.length === 0);
  const llista = (cerca ? resultats.data : recents.data)?.items ?? [];

  const envia = (e: FormEvent) => {
    e.preventDefault();
    const v = text.trim();
    if (/^\d+$/.test(v)) navigate(`/analisi/tracabilitat/${v}`);
    else setCerca(v);
  };

  return (
    <section className="pantalla" data-screen="W-45">
      <Toolbar titol={t('titols.tracabilitat')} />
      <div className="content stack">
        <form className="tracabilitat__cerca" onSubmit={envia}>
          <label className="search">
            <Icona nom="magnifier" />
            <input
              aria-label={t('w45.cerca')}
              placeholder={t('w45.cercaPlaceholder')}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </label>
          <button type="submit" className="btn btn--primary btn--s">
            {t('w45.cercar')}
          </button>
        </form>
        <div className="panel">
          <div className="panel__head">
            <h3>{cerca ? t('w45.resultats') : t('w45.recents')}</h3>
          </div>
          {llista.length === 0 ? (
            <Buit icona="magnifier">{t('comu.senseDades')}</Buit>
          ) : (
            <div>
              {llista.map((a) => (
                <Link key={a.id} className="list-row" to={`/analisi/tracabilitat/${a.id}`}>
                  <div className={`list-row__icon ${a.critica ? 'bg-red' : 'bg-blue'}`}>
                    <Icona nom="link" />
                  </div>
                  <div className="list-row__text">
                    <div className="list-row__label">
                      <span className="codi">{a.codi}</span> · {a.titol}
                    </div>
                    <div className="list-row__sub">{a.servei ?? '—'}</div>
                  </div>
                  <BadgeEstat estat={a.estat} />
                  <Icona nom="chevron-right" className="list-row__chev c-gray" />
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Detall({ id }: { id: number }) {
  const { t } = useTranslation(['informes', 'comu']);
  const f = useFormat();
  const idioma = useIdioma();
  const temps = useTemps();
  const etiqueta = useEtiquetaEvent();
  const etiquetaIndicador = useEtiquetaIndicador();
  const activacio = useActivacio(id);
  const traca = useTracabilitat(id);
  const [error, setError] = useState(false);
  const a = activacio.data;
  const d = traca.data;

  const exporta = async () => {
    if (!d) return;
    setError(false);
    try {
      await descarregaInformeActivacio(d.activacioId, d.codi, 'PDF', idioma);
    } catch {
      setError(true);
    }
  };

  const cronologia = [...(d?.cronologia ?? [])].sort((x, y) => x.at.localeCompare(y.at));
  const hora = (iso: string) =>
    new Intl.DateTimeFormat(idioma === 'es' ? 'es-ES' : 'ca-ES', {
      timeZone: 'Europe/Madrid',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(new Date(iso));

  return (
    <section className="pantalla" data-screen="W-45">
      <Toolbar
        titol={d ? t('w45.titol', { codi: d.codi }) : t('titols.tracabilitat')}
        enrere="/analisi/tracabilitat"
        badges={
          a ? (
            <>
              <BadgeTipus tipus={a.tipus} critica={a.critica} />
              <BadgeEstat estat={a.estat} />
            </>
          ) : null
        }
      >
        <Link className="btn btn--s" to={`/analisi/informes/activacio/${id}`}>
          <Icona nom="doc-text" />
          {t('w45.informe')}
        </Link>
        <button type="button" className="btn btn--s" disabled={!d} onClick={() => void exporta()}>
          <Icona nom="doc-export" />
          {t('w45.exporta')}
        </button>
      </Toolbar>
      <div className="content stack">
        {error ? (
          <div className="avis-banda" role="alert">
            <Icona nom="exclamationmark-triangle" />
            {t('w42.errors.descarrega')}
          </div>
        ) : null}
        {traca.isError ? (
          <ErrorCarrega onReintenta={() => void traca.refetch()} />
        ) : !d ? (
          <Buit icona="link">{t('comu.carregant')}</Buit>
        ) : (
          <div className="grid grid-sidebar">
            <div className="panel">
              <div className="panel__head">
                <h3>{t('w45.cronologia')}</h3>
                <div className="spacer" />
                {a ? <span className="t-caption-2 dim truncate">{a.titol}</span> : null}
              </div>
              <div className="panel__body">
                {cronologia.length === 0 ? (
                  <Buit icona="clock">{t('comu.senseDades')}</Buit>
                ) : (
                  <ol className="timeline" data-testid="cronologia">
                    {cronologia.map((e, i) => (
                      <li
                        key={`${e.at}-${i}`}
                        className={`tl-item tl-item--${colorEvent(e.tipus)}`}
                      >
                        <div className="tl-time">
                          {hora(e.at)} · {f.data(e.at)}
                        </div>
                        <div className="tl-title">{etiqueta(e.tipus)}</div>
                        <div className="tl-text">
                          {[
                            e.usuari,
                            e.canal ? t(`comu:canal.${e.canal}`, { defaultValue: e.canal }) : null,
                            e.detall,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
            <div className="stack">
              <div className="panel">
                <div className="panel__head">
                  <h3>{t('w45.temps')}</h3>
                </div>
                <div className="panel__body temps-kv" data-testid="temps">
                  {d.temps.length === 0 ? (
                    <Buit icona="clock">{t('comu.senseDades')}</Buit>
                  ) : (
                    d.temps.map((x) => {
                      const color = colorCompliment(x.complert);
                      return (
                        <div className="kv" key={x.indicador}>
                          <span className="kv__k">
                            {etiquetaIndicador(x.indicador)}
                            {x.compromesSeg != null ? (
                              <span className="t-caption-2">
                                {' '}
                                {t('w45.objectiu', { temps: temps(x.compromesSeg) })}
                              </span>
                            ) : null}
                          </span>
                          <span className={`kv__v ${color}`}>
                            {temps(x.realSeg)} ·{' '}
                            <span>
                              {x.complert == null
                                ? t('w45.pendent')
                                : x.complert
                                  ? t('w45.complert')
                                  : t('w45.noComplert')}
                            </span>
                          </span>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              <div className="panel">
                <div className="panel__head">
                  <h3>{t('w45.resum')}</h3>
                </div>
                <div className="panel__body">
                  {RESUM.map(([clau, compte]) => [clau, compte(cronologia)] as const).map(
                    ([clau, n]) => (
                      <div className="kv" key={clau}>
                        <span className="kv__k">{t(`w45.${clau}`)}</span>
                        <span className={`kv__v${clau === 'escalats' && n > 0 ? ' c-red' : ''}`}>
                          {f.numero(n)}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export function W45Tracabilitat() {
  const param = useParams().id;
  const id = param ? Number(param) : NaN;
  return Number.isFinite(id) ? <Detall key={id} id={id} /> : <Cerca />;
}
